import { useQuery } from '@tanstack/react-query'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useState } from 'react'
import { z } from 'zod'
import { boardsQueryOptions } from '@/entities/board'
import { CreateProjectDialog } from '@/features/create-project'
import {
  activeFilterCount,
  BoardFilterDialog,
  BoardFilterSearchSchema,
  boardFilterFromSearch,
  boardFilterToSearch,
  EMPTY_BOARD_FILTER,
  toBoardFilters,
} from '@/features/filter-board'
import { LossReasonsDialog } from '@/features/manage-board'
import { Button } from '@/shared/ui/button'
import { Chip } from '@/shared/ui/chip'
import { Icon } from '@/shared/ui/icon'
import { Menu } from '@/shared/ui/menu'
import { PageHeader } from '@/shared/ui/page-header'
import { Spinner } from '@/shared/ui/spinner'
import { StatusScreen } from '@/shared/ui/status-screen'
import { ZoneNote } from '@/shared/ui/zone-badge'
import { BoardTabs, ProjectBoard } from '@/widgets/project-board'

// board — выбранная воронка (пусто = первая в переключателе). Остальной фильтр
// (наборы ответственных/приоритетов/тегов/компаний, срок, скрытые-архивные)
// живёт в адресе по схеме фичи: ссылка шарится и переживает перезагрузку.
const BoardsSearchSchema = BoardFilterSearchSchema.extend({
  board: z.string().optional().catch(undefined),
})

// Воронки — главный экран сотрудника. Заказчику здесь нечего делать.
export const Route = createFileRoute('/_app/boards')({
  validateSearch: (search) => BoardsSearchSchema.parse(search),
  beforeLoad: ({ context }) => {
    if (context.session?.kind === 'customer') throw redirect({ to: '/projects' })
  },
  head: () => ({ meta: [{ title: 'Воронки · STIT Deal' }] }),
  component: BoardsPage,
})

function BoardsPage() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const [createOpen, setCreateOpen] = useState(false)
  const [lossReasonsOpen, setLossReasonsOpen] = useState(false)
  const [filterOpen, setFilterOpen] = useState(false)

  const boardsQ = useQuery(boardsQueryOptions)
  const boards = boardsQ.data ?? []
  // Воронка из адреса; её нет (удалена в соседней вкладке) — первая в ряду.
  const active = boards.find((b) => b.id === search.board) ?? boards[0]

  const filter = boardFilterFromSearch(search)
  const activeCount = activeFilterCount(filter)

  const goTo = (patch: Record<string, unknown>) =>
    void navigate({
      search: { ...(active ? { board: active.id } : {}), ...boardFilterToSearch(filter), ...patch },
    })
  const applyFilter = (next: typeof filter) =>
    void navigate({
      search: { ...(active ? { board: active.id } : {}), ...boardFilterToSearch(next) },
    })

  return (
    <div className="flex h-full flex-col p-4 md:p-6">
      <PageHeader
        title="Воронки"
        actions={
          <>
            {filter.tags.map((tag) => (
              <Chip key={tag} variant="soft" className="gap-1 pr-1">
                <span className="text-text-tertiary">Тег:</span>
                <span className="text-text-primary">{tag}</span>
                <button
                  type="button"
                  aria-label={`Сбросить фильтр по тегу «${tag}»`}
                  onClick={() =>
                    applyFilter({ ...filter, tags: filter.tags.filter((t) => t !== tag) })
                  }
                  className="rounded-full p-0.5 text-text-tertiary transition-colors hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent"
                >
                  <Icon name="close" size={14} />
                </button>
              </Chip>
            ))}
            {/* «Что горит» — в один клик, мимо модалки. */}
            <Button
              variant={filter.deadline === 'overdue' ? 'primary' : 'ghost'}
              size="sm"
              aria-pressed={filter.deadline === 'overdue'}
              onClick={() =>
                applyFilter({
                  ...filter,
                  deadline: filter.deadline === 'overdue' ? undefined : 'overdue',
                  from: undefined,
                  to: undefined,
                })
              }
            >
              <Icon name="clock" size={16} />
              Просрочено
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setFilterOpen(true)}>
              <Icon name="filter" size={16} />
              Фильтр
              {activeCount > 0 && (
                <span className="mono-num rounded-pill bg-accent px-1.5 text-text-inverse text-xs">
                  {activeCount}
                </span>
              )}
            </Button>
            {activeCount > 0 && (
              <Button variant="ghost" size="sm" onClick={() => applyFilter(EMPTY_BOARD_FILTER)}>
                Сбросить
              </Button>
            )}
            <Menu
              aria-label="Настройки воронок"
              trigger={<Icon name="dots" size={18} />}
              triggerClassName="p-2"
              items={[
                {
                  label: 'Причины проигрыша…',
                  icon: 'settings',
                  onSelect: () => setLossReasonsOpen(true),
                },
              ]}
            />
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Icon name="plus" size={16} />
              Проект
            </Button>
          </>
        }
      />

      {boardsQ.isPending ? (
        <div className="flex flex-1 items-center justify-center py-24">
          <Spinner label="Загрузка воронок" />
        </div>
      ) : boardsQ.isError || !active ? (
        <StatusScreen
          title="Не удалось загрузить воронки"
          message="Попробуйте обновить страницу позже."
        />
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <BoardTabs
              boards={boards}
              activeId={active.id}
              onSelect={(id) => goTo({ board: id })}
            />
            {/* Срок шага на карточке — момент. Плиток на доске десятки, тег у
                каждой был бы шумом: зона названа один раз на доску. */}
            <ZoneNote what="Сроки шагов" />
          </div>
          <div className="min-h-0 flex-1">
            <ProjectBoard
              board={active}
              otherBoards={boards.filter((b) => b.id !== active.id)}
              filters={toBoardFilters(filter, active.id)}
            />
          </div>
        </>
      )}

      <CreateProjectDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        {...(active ? { boardId: active.id } : {})}
      />
      {lossReasonsOpen && <LossReasonsDialog open onClose={() => setLossReasonsOpen(false)} />}
      {filterOpen && (
        <BoardFilterDialog
          open
          onClose={() => setFilterOpen(false)}
          value={filter}
          onApply={(next) => {
            applyFilter(next)
            setFilterOpen(false)
          }}
        />
      )}
    </div>
  )
}
